import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BookmarksListModal } from '../BookmarksListModal';
import { EmptyBookmarksModal } from '../EmptyBookmarksModal';
import * as S from './EmptyCollectionScreen.styled';
import { GET_USER_BOOKMARKS } from '@/api/queries/user.queries';
import { GetUserBookmarksResponse } from '@/api/types/user.types';
import { UniversalPlusIcon } from '@/components/UI/Icons/Icons';
import { useIsTopBannerVisible } from '@/hooks/useIsTopBannerVisible';

interface EmptyCollectionScreenProps {
  isOwner: boolean;
}

export const EmptyCollectionScreen = ({
  isOwner,
}: EmptyCollectionScreenProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isTopBannerVisible = useIsTopBannerVisible();

  const { data } = useQuery<GetUserBookmarksResponse>(GET_USER_BOOKMARKS, {
    variables: { first: 1 },
  });

  const bookmarks = data?.currentUser?.bookmarks?.nodes ?? [];
  const [isBookmarksListModalOpen, setIsBookmarksListModalOpen] =
    useState(false);

  const handleAddBookmarks = () => {
    setIsBookmarksListModalOpen(true);
  };

  return (
    <S.EmptyCollectionScreen
      $isTopBannerVisible={isTopBannerVisible}
      data-testid="collections-empty"
    >
      <S.Title>{t('bookmark_collections.empty_collection_title')}</S.Title>
      {isOwner ? (
        <>
          <S.Description>
            {t('bookmark_collections.empty_collection_description')}
          </S.Description>
          <S.AddButton
            onClick={handleAddBookmarks}
            data-testid="collection-add-bookmarks-button"
          >
            <UniversalPlusIcon
              width={13}
              height={13}
              fill={theme.palette.system.white}
            />

            {t('bookmark_collections.drawer_add_button')}
          </S.AddButton>
        </>
      ) : null}

      {bookmarks.length ? (
        <BookmarksListModal
          isOpen={isBookmarksListModalOpen}
          onClose={() => setIsBookmarksListModalOpen(false)}
        />
      ) : (
        <EmptyBookmarksModal
          isOpen={isBookmarksListModalOpen}
          onClose={() => setIsBookmarksListModalOpen(false)}
        />
      )}
    </S.EmptyCollectionScreen>
  );
};
